import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import {
  selectCustomerSourceTypes,
  selectCustomerSourceTypesIsEmpty,
  selectCustomerSourceTypesLoading,
} from 'store/features/customer-source-types/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import customerSourceTypesActions from 'store/features/customer-source-types/actions';
import CreateCustomerSourceTypeDialog from './components/create-customer-source-type-dialog';
import UpdateCustomerSourceTypeDialog from './components/update-customer-source-type-dialog';

const CustomerSourceTypes = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectCustomerSourceTypesLoading);
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const customerSourceTypes = useAppSelector(selectCustomerSourceTypes);
  const isEmptyCustomerSourceTypes = useAppSelector(selectCustomerSourceTypesIsEmpty);

  // useState hooks
  const [openDeleteCustomerSourceType, setOpenDeleteCustomerSourceType] = useState(false);
  const [openCreateCustomerSourceType, setOpenCreateCustomerSourceType] = useState(false);
  const [openUpdateCustomerSourceType, setOpenUpdateCustomerSourceType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'customerSourceTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(customerSourceTypesActions.getCustomerSourceTypes());
  };

  // sync helper functions
  const handleCloseDeleteCustomerSourceType = () => setOpenDeleteCustomerSourceType(false);
  const handleClickOpenDeleteCustomerSourceType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteCustomerSourceType(true);
  };

  const handleCloseCreateCustomerSourceType = () => setOpenCreateCustomerSourceType(false);
  const handleClickOpenCreateCustomerSourceType = () => setOpenCreateCustomerSourceType(true);

  const handleCloseUpdateCustomerSourceType = () => setOpenUpdateCustomerSourceType(false);
  const handleClickOpenUpdateCustomerSourceType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateCustomerSourceType(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-customer-source-type')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!customerSourceTypes.length && !isEmptyCustomerSourceTypes) {
      dispatch(customerSourceTypesActions.getCustomerSourceTypes());
    }
  }, [customerSourceTypes, isEmptyCustomerSourceTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-customer-source-type']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenCreateCustomerSourceType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage
                id="customerSourceTypes.createCustomerSourceType"
                defaultMessage="Müşteri Kaynağı Oluştur"
              />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-customer-source-type']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenUpdateCustomerSourceType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage
                id="customerSourceTypes.updateCustomerSourceType"
                defaultMessage="Müşteri Kaynağı Düzenle"
              />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-customer-source-type']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenDeleteCustomerSourceType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage
                id="customerSourceTypes.deleteCustomerSourceType"
                defaultMessage="Müşteri Kaynağı Sil"
              />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateCustomerSourceTypeDialog
        open={openCreateCustomerSourceType}
        handleClose={handleCloseCreateCustomerSourceType}
        refresh={refresh}
      />
      <UpdateCustomerSourceTypeDialog
        open={openUpdateCustomerSourceType}
        customerSourceTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateCustomerSourceType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteCustomerSourceType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteCustomerSourceType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={
          <FormattedMessage id="customerSourceTypes.deleteCustomerSourceType" defaultMessage="Müşteri Kaynağı Sil" />
        }
        subTitle={
          <FormattedMessage
            id="customerSourceTypes.deleteCustomerSourceTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra müşteri kaynağı veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteCustomerSourceType}
        successMessage={
          <FormattedMessage
            id="customerSourceTypes.deleteCustomerSourceTypeCompleted"
            defaultMessage="Seçili müşteri kaynağını silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={customerSourceTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default CustomerSourceTypes;
